import {
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Divider,
  Paper,
  Alert,
  TextField,
} from '@mui/material'
import {
  Assignment,
  Code,
  EmailOutlined,
  List,
  ReceiptOutlined,
  ExpandMore as ExpandMoreIcon,
  Warning,
} from '@mui/icons-material'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import { CheckboxWithLabel } from 'formik-mui'
import { inject, observer } from 'mobx-react'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import AuthorizationLogGrid from 'src/authorizationLog/AuthorizationLogGrid'
import { EOCEmailType } from 'src/models/enums/EOCEmailType.enum'
import {
  generateEOCEmail,
  getQuoteSummaryMessage,
  logEOCEmailGeneration,
} from 'src/services/EpisodeOfCareService'
import * as Yup from 'yup'
import PatientActionBar from '../../common/ActionBar/PatientActionBar'
import CardWithNoTitle from '../../common/CardWithNoTitle/CardWithNoTitle'
import DatePickerField from '../../common/DatePickerField/DatePickerField'
import ESDialog from '../../common/ESDialog'
import FullWidthField from '../../common/FullWidthField'
import ResponsiveAppBar from '../../common/ResponsiveAppBar'
import SelectField from '../../common/SelectField/SelectField'
import { noWhitespaceString } from '../../common/YupExtensions'
import { useUnsavedChangesWarning } from '../../common/hooks/useUnsavedChangesWarning'
import UnsavedChangesDialog from '../../common/UnsavedChangesDialog/UnsavedChangesDialog'
import Constants from '../../config'
import {
  DefaultCptCode,
  DefaultDxCode,
  DefaultEpisodeOfCare,
  ICptCode,
  IDxCode,
  IEpisodeOfCare,
  IOrganization,
  IPatient,
  IReferringDoctor,
  IUser,
} from '../../Definitions'
import { CptCodeDto, DxCodeDto, SurgeryCodeDto } from '../../generated_client'
import { IStores } from '../../Stores'
import {
  CoordinationStatus,
  CoordinationStatusLabels,
} from '../../utils/CoordinationStatus'
import EOCStatus from '../../utils/EOCStatus'
import SchedulingTable from '../Scheduling/SchedulingTable'
import CodeExpandingList from './CodeExpandingList'
import WorkRestrictionLogModal from './WorkRestrictionLogModal'
import {
  createIEpisodeOfCareFormValues,
  IEpisodeOfCareFormValues,
  toIEpisodeOfCare,
} from './EpisodeOfCareFormValues'
import moment from 'moment'
import ReferralContactModal from './ReferralContactModal'
import {
  IEpisodeOfCareQuoteButton,
  IEpisodeOfCareQuoteSummary,
} from 'src/viewModels/workflow/EpisodeOfCareQuoteSummary'

interface INewSurgeryCodeFormValues {
  cptCodes: ICptCode[]
  dxCodes: IDxCode[]
}

const EpisodeOfCareSurgicalSchema = Yup.object().shape({
  bodyPartText: noWhitespaceString('Body Part is Required', true),
  careCoordinatorId: noWhitespaceString().required().label('Care Coordinator'),
  cptCodes: Yup.array().of(
    Yup.object().shape({
      code: Yup.string(),
      priority: Yup.number(),
    })
  ),
  ctScan: Yup.string(),
  dxCodes: Yup.array().of(
    Yup.object().shape({
      code: Yup.string(),
      priority: Yup.number(),
    })
  ),
  effectiveDate: Yup.date().when(
    'invoiceSurgeryStatus',
    ([invoiceSurgeryStatus], schema) => {
      return invoiceSurgeryStatus !== undefined
        ? schema.required().label('Takeover of Care Date')
        : schema
    }
  ),
  emg: Yup.string(),
  episodeOfCareNumber: Yup.string(),
  invoiceStatus: Yup.string(),
  invoiceSurgeryStatus: Yup.string(),
  isClinicalNotesReceived: Yup.bool(),
  isNoPtToBeScheduled: Yup.bool(),
  isPatientOnBoarding: Yup.bool(),
  mri: Yup.string(),
  notes: Yup.string(),
  tests: Yup.array().of(
    Yup.object().shape({
      bodyPartId: noWhitespaceString('Body Part is required', true),
      cptCodes: Yup.array().of(
        Yup.object().shape({
          code: Yup.string(),
          priority: Yup.number(),
        })
      ),
      diagnosis: Yup.string(),
      dxCodes: Yup.array().of(
        Yup.object().shape({
          code: Yup.string(),
          priority: Yup.number(),
        })
      ),
      followUpDateTime: Yup.date(),
      notes: Yup.string(),
      procedureId: noWhitespaceString('Procedure is required', true),
      rxReceived: Yup.bool(),
      status: Yup.string(),
      testIssue: Yup.bool(),
    })
  ),
  xRay: Yup.string(),
})

interface IUnsavedChangesProps {
  showCancelDialog: boolean
  handleCancelClick: (hasChanges: boolean) => void
  handleCancelConfirm: (onNavigate: () => void) => void
  handleCancelDismiss: () => void
  setIsDirty: (isDirty: boolean) => void
}

interface IEditEpisodeOfCareProps extends IUnsavedChangesProps {
  patientId?: string
  referralId?: string
  episodeOfCareId?: string
  closeCodeCheckModal?: () => void
  closeTab?: (fallBackPath?: string) => void
  cptCodes?: ICptCode[]
  currentAppOrganization?: IOrganization
  dxCodes?: IDxCode[]
  referringDoctors?: IReferringDoctor[]
  getAllUsers?: (pageSize?: number, roleFilter?: string) => Promise<void>
  getAllCptCodes?: (cptCodesList?: string[]) => Promise<void>
  getAllDxCodes?: (dxCodesList?: string[]) => Promise<void>
  getPatientById?: (id: string) => Promise<void>
  getEpisodeOfCareById?: (id: string) => Promise<void>
  getReferringDoctors?: (filter: string) => Promise<void>
  isCodeCheckModalOpen?: boolean
  newCptCodesToSave?: SurgeryCodeDto[]
  newDxCodesToSave?: SurgeryCodeDto[]
  openCodeCheckModal?: () => void
  patient?: IPatient | undefined
  episodeOfCare?: IEpisodeOfCare
  rerouteToPath?: (path: string) => void
  returnRoutePath?: string
  saveCptCode?: (cptCode: ICptCode) => Promise<void>
  saveDxCode?: (dxCode: IDxCode) => Promise<void>
  saveEpisodeOfCare?: (episodeOfCare: IEpisodeOfCare) => Promise<void>
  setNewCptCodesToSave?: (cptCodes: SurgeryCodeDto[]) => void
  setNewDxCodesToSave?: (dxCodes: SurgeryCodeDto[]) => void
  setSelectedEpisodeOfCare?: (episodeOfCare: IEpisodeOfCare | undefined) => void
  setSelectedPatient?: (patient: IPatient | undefined) => void
  users?: IUser[]
  setReturn?: (path?: string) => Promise<void>
  eocToDashboard?: boolean
  eocToQuotes?: boolean
  resetEOC?: () => void
  openReferralContactModal?: () => void
  isReferralContactModalOpen?: boolean
}

interface IEditEpisodeOfCareState {
  expandedDetailsPanel: string
  expandedSchedulePanel: string
  expandedLogPanel: string
  isDelete: boolean
  reload: boolean
  emailContent: string
  generateEmail: EOCEmailType
  isEOCSaved: boolean
  quoteSummary: IEpisodeOfCareQuoteSummary | undefined
  isLoading: boolean
  isWorkRestrictionLogModalOpen: boolean
  totalPtCount: number
  quotedPtCount: number
}
@observer
class EditEpisodeOfCare extends React.Component<
  IEditEpisodeOfCareProps,
  IEditEpisodeOfCareState
> {
  constructor(props: IEditEpisodeOfCareProps) {
    super(props)
    this.state = {
      expandedDetailsPanel:
        props.episodeOfCare && props.episodeOfCare.isNew ? 'detailsPanel' : '',
      expandedSchedulePanel:
        props.episodeOfCare && props.episodeOfCare.isNew ? '' : 'schedulePanel',
      expandedLogPanel:
        props.episodeOfCare && props.episodeOfCare.isNew ? '' : 'logPanel',
      isDelete: false,
      reload: false,
      emailContent: '',
      generateEmail: EOCEmailType.NewSurgeryEmail,
      isEOCSaved: false,
      quoteSummary: undefined,
      isLoading: true,
      isWorkRestrictionLogModalOpen: false,
      totalPtCount: 0,
      quotedPtCount: 0,
    }
  }

  private async setup() {
    const episodeOfCareId = this.props.episodeOfCareId
    this.setState({
      isDelete: false,
      isEOCSaved: false,
      isLoading: true,
    })
    this.setState({
      expandedDetailsPanel: episodeOfCareId ? '' : 'detailsPanel',
      expandedSchedulePanel: episodeOfCareId ? 'schedulePanel' : '',
    })
    const promises = [] as Array<Promise<void>>

    if (episodeOfCareId) {
      promises.push(this.props.getEpisodeOfCareById!(episodeOfCareId))
      generateEOCEmail(episodeOfCareId, this.state.generateEmail).then((result) => {
        this.setState({ emailContent: result })
      })
      getQuoteSummaryMessage(episodeOfCareId).then((result) => {
        this.setState({
          quoteSummary: result,
          totalPtCount: result.totalPtCount ?? 0,
          quotedPtCount: result.quotedPtCount ?? 0,
        })
      })
    } else {
      this.props.setSelectedEpisodeOfCare!(DefaultEpisodeOfCare(true))
    }

    const patientId = this.props.patientId

    if (patientId) {
      promises.push(this.props.getPatientById!(patientId))
    }

    promises.push(this.props.getAllUsers!(5000, Constants.ObxRoles.CareCoordinator))

    promises.push(this.props.getReferringDoctors!(''))

    await Promise.all(promises).finally(() => {
      this.setState({ isLoading: false })
    })
  }

  public async componentDidMount() {
    await this.setup()
  }

  public async componentDidUpdate(prevProps: IEditEpisodeOfCareProps) {
    if (this.props.episodeOfCareId !== prevProps.episodeOfCareId) {
      await this.setup()
    }
  }

  public componentWillUnmount() {
    this.props.setSelectedEpisodeOfCare!(undefined)
    this.props.setSelectedPatient!(undefined)
  }

  private refreshPtCounts = () => {
    const episodeOfCareId = this.props.episodeOfCareId
    if (episodeOfCareId) {
      getQuoteSummaryMessage(episodeOfCareId).then((result) => {
        this.setState({
          quoteSummary: result,
          totalPtCount: result.totalPtCount ?? 0,
          quotedPtCount: result.quotedPtCount ?? 0,
        })
      })
    }
  }

  public save = (
    values: IEpisodeOfCareFormValues,
    formikBag: FormikHelpers<IEpisodeOfCareFormValues>
  ) => {
    this.setState({ isEOCSaved: true, isLoading: true })
    const { currentAppOrganization } = this.props
    const episodeOfCare = toIEpisodeOfCare(values, this.props.episodeOfCare)
    episodeOfCare.cptCodes.forEach((x: CptCodeDto) => (x.code = x.code!.toUpperCase()))
    episodeOfCare.dxCodes.forEach((x: CptCodeDto) => (x.code = x.code!.toUpperCase()))
    if (this.props.episodeOfCare!.isNew) {
      episodeOfCare.organizationId = currentAppOrganization!.id
      episodeOfCare.referralId = this.props.referralId!
    }
    const cptCodesList: SurgeryCodeDto[] = episodeOfCare.cptCodes
    const dxCodesList: SurgeryCodeDto[] = episodeOfCare.dxCodes

    this.props.saveEpisodeOfCare!(episodeOfCare)
      .then(() => {
        formikBag.setSubmitting(false)
        const validCptCodes = cptCodesList
          .filter((y) => y.code && y.code.trim())
          .map((y) => y.code!.trim())
        const validDxCodes = dxCodesList
          .filter((y) => y.code && y.code.trim())
          .map((y) => y.code!.trim())

        const getAllCptCodes =
          validCptCodes.length > 0
            ? this.props.getAllCptCodes!(validCptCodes)
            : Promise.resolve()
        const getAllDxCodes =
          validDxCodes.length > 0
            ? this.props.getAllDxCodes!(validDxCodes)
            : Promise.resolve()

        Promise.all([getAllCptCodes, getAllDxCodes])
          .then(() => {
            const newCptCodes = cptCodesList.filter(
              (x) =>
                this.props.cptCodes!.find(
                  (y) => y.code.trim().toLowerCase() === x.code!.trim().toLowerCase()
                ) === undefined
            )
            const newDxCodes = dxCodesList.filter(
              (x) =>
                this.props.dxCodes!.find(
                  (y) => y.code.trim().toLowerCase() === x.code!.trim().toLowerCase()
                ) === undefined
            )
            this.props.setNewCptCodesToSave!(newCptCodes)
            this.props.setNewDxCodesToSave!(newDxCodes)
            if (newCptCodes.length > 0 || newDxCodes.length > 0) {
              //refresh on save and then show modal to facilitate not now on save
              this.props.rerouteToPath!(
                `/patients/patient/${this.props.patientId}/referral/${this.props.referralId}/episodeOfCare/${episodeOfCare.id}`
              )
              this.setup()
              this.props.openCodeCheckModal!()
            } else {
              if (this.state.reload) {
                this.props.rerouteToPath!(
                  `/patients/patient/${this.props.patientId}/referral/${this.props.referralId}/episodeOfCare/${episodeOfCare.id}`
                )
                this.setup()
              } else {
                this.setState({ isLoading: false })
                this.cancel()
              }
            }
          })
          .catch((_) => {
            if (!this.state.reload) {
              this.setState({ isLoading: false })
              this.cancel()
            } else {
              this.setState({ isLoading: false })
            }
          })
      })
      .catch(() => {
        formikBag.setSubmitting(false)
        this.setState({ isLoading: false })
      })
  }

  private handleCancelClick = (dirty: boolean) => {
    if (dirty) {
      this.props.handleCancelClick(dirty)
    } else {
      this.cancel()
    }
  }

  private cancel = () => {
    ;(window as any)._skipBeforeUnload = true
    this.props.setSelectedEpisodeOfCare!(undefined)
    if (this.props.eocToDashboard) {
      this.props.setReturn!('/')
      this.props.resetEOC!()
    } else if (this.props.eocToQuotes) {
      this.props.setReturn!('/quotes')
      this.props.resetEOC!()
    } else if (sessionStorage.getItem('eocTab') === '1') {
      this.props.setReturn!()
    } else if (this.props.returnRoutePath === undefined) {
      this.props.setReturn!(`/patients/patient/${this.props.patientId}`)
    }
    this.props.closeTab!()
    setTimeout(() => {
      ;(window as any)._skipBeforeUnload = false
    }, 1000)
  }

  public getValue(item: string) {
    return item
  }

  public getUserName(item: IUser) {
    return item ? item.name : ''
  }

  public getUserValue(item: IUser) {
    return item ? item.id : ''
  }

  private handleSave = (submitForm: () => void, reload: boolean) => () => {
    this.setState({ reload }, () => {
      submitForm()
    })
  }

  private handleDetailsChange =
    (panel: string) => (_: React.SyntheticEvent, expanded: boolean) => {
      this.setState({
        expandedDetailsPanel: expanded ? panel : '',
      })
    }

  private handleScheduleChange =
    (panel: string) => (_: React.SyntheticEvent, expanded: boolean) => {
      this.setState({
        expandedSchedulePanel: expanded ? panel : '',
      })
    }

  private handleLogChange =
    (panel: string) => (_: React.SyntheticEvent, expanded: boolean) => {
      this.setState({
        expandedLogPanel: expanded ? panel : '',
      })
    }

  private generateEmailClick = (values: IEpisodeOfCareFormValues) => () => {
    logEOCEmailGeneration(values.id, this.state.generateEmail)
  }

  private handleGenerateEmailChange = (event: any) => {
    this.setState({ generateEmail: event.target.value })
    generateEOCEmail(this.props.episodeOfCare!.id, event.target.value).then((result) => {
      this.setState({ emailContent: result })
    })
  }

  private hasWorkRestrictionDates = (episodeOfCare: IEpisodeOfCare): boolean => {
    return !!(
      episodeOfCare.lightDutyScheduleAppointmentTaskCompletionDate ||
      episodeOfCare.lightDutyPermanentScheduleAppointmentTaskCompletionDate ||
      episodeOfCare.fullDutyScheduleAppointmentTaskCompletionDate ||
      episodeOfCare.unableToWorkScheduleAppointmentTaskCompletionDate
    )
  }

  private handleWorkRestrictionLogClick = () => {
    this.setState({ isWorkRestrictionLogModalOpen: true })
  }

  private closeWorkRestrictionLogModal = () => {
    this.setState({ isWorkRestrictionLogModalOpen: false })
  }

  private renderQuoteButtonIcon = (button: IEpisodeOfCareQuoteButton) => {
    const iconStyle: React.CSSProperties = {
      fontSize: 'large',
      verticalAlign: 'middle',
      display: 'inline-flex',
      alignItems: 'center',
      paddingBottom: '4px',
      cursor: 'default',
      marginRight: '8px',
    }

    if (!button.hasBeenConsumed && !button.isConsumer) {
      return <List style={{ marginRight: '8px' }} />
    }

    if (button.hasBeenConsumed) {
      return <span style={iconStyle}>🐠</span>
    }

    if (button.isConsumer) {
      return <span style={iconStyle}>🦈</span>
    }

    return null
  }

  private getQuoteButtonLabel = (
    button: IEpisodeOfCareQuoteButton,
    buttonCount: number
  ) => {
    return buttonCount === 1 ? 'View Quote' : `Quote ${button.groupNumber}`
  }

  public render() {
    const surgicalStatusOptions = [
      { value: EOCStatus.Cancelled, label: EOCStatus.Cancelled },
      { value: EOCStatus.Evaluation, label: EOCStatus.Evaluation },
      { value: EOCStatus.CaseClosed, label: EOCStatus.CaseClosed },
      { value: EOCStatus.InjectionBundle, label: EOCStatus.InjectionBundle },
      { value: EOCStatus.NonSurgical, label: EOCStatus.NonSurgical },
      { value: EOCStatus.NonSurgicalBundle, label: EOCStatus.NonSurgicalBundle },
      { value: EOCStatus.PotentialNonSurgical, label: EOCStatus.PotentialNonSurgical },
      { value: EOCStatus.SurgicalBundle, label: EOCStatus.SurgicalBundle },
      { value: EOCStatus.SxPostponed, label: EOCStatus.SxPostponed },
    ]

    const {
      currentAppOrganization,
      episodeOfCare,
      users,
      isCodeCheckModalOpen,
      newCptCodesToSave,
      newDxCodesToSave,
      referringDoctors,
      isReferralContactModalOpen,
    } = this.props

    const { quoteSummary } = this.state
    const title =
      episodeOfCare && episodeOfCare.isNew
        ? 'Add New Episode of Care'
        : 'Edit Episode of Care'
    const shortenedTitle =
      episodeOfCare && episodeOfCare.isNew ? 'Add New EOC' : 'Edit EOC'

    const newCodeFormValues = {
      cptCodes: newCptCodesToSave
        ? newCptCodesToSave.map((x) => ({ code: x.code }) as ICptCode)
        : [],
      dxCodes: newDxCodesToSave
        ? newDxCodesToSave.map((x) => ({ code: x.code }) as ICptCode)
        : [],
    } as INewSurgeryCodeFormValues

    const readonly =
      episodeOfCare && currentAppOrganization
        ? episodeOfCare.isNew
          ? false
          : episodeOfCare.organizationId !== currentAppOrganization!.id
        : true

    return (
      <>
        <Helmet>
          <title>
            {shortenedTitle +
              ': ' +
              this.props.patient?.lastName +
              ', ' +
              this.props.patient?.firstName +
              ' (' +
              moment(this.props.patient?.dateOfBirth).format('MM/DD/YYYY') +
              ')' +
              ' ' +
              this.props.episodeOfCare?.episodeOfCareNumberOrLegacyId}
          </title>
        </Helmet>
        {!episodeOfCare && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        {episodeOfCare && (
          <>
            {isReferralContactModalOpen && (
              <ReferralContactModal selectedReferralId={this.props.referralId} />
            )}
            {this.state.isWorkRestrictionLogModalOpen && (
              <WorkRestrictionLogModal
                isOpen={this.state.isWorkRestrictionLogModalOpen}
                closeModal={this.closeWorkRestrictionLogModal}
                episodeOfCareId={episodeOfCare.id}
                patientHeader={`${this.props.patient?.lastName}, ${
                  this.props.patient?.firstName
                } (${moment(this.props.patient?.dateOfBirth).format(
                  'MM/DD/YYYY'
                )}) ${episodeOfCare.episodeOfCareNumberOrLegacyId}`}
              />
            )}
            <Formik
              initialValues={createIEpisodeOfCareFormValues(episodeOfCare)}
              onSubmit={this.save}
              validationSchema={EpisodeOfCareSurgicalSchema}
              enableReinitialize={true}
            >
              {({ isValid, values, setFieldValue, errors, submitForm, dirty }) => {
                // Update isDirty through props
                React.useEffect(() => {
                  this.props.setIsDirty(dirty)
                }, [dirty])

                return (
                  <Form>
                    {this.state.isLoading && (
                      <div
                        style={{
                          position: 'fixed',
                          display: 'flex',
                          width: '100%',
                          height: '100%',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          cursor: 'pointer',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: '1200',
                        }}
                      >
                        <CircularProgress size={100} />
                      </div>
                    )}

                    <ResponsiveAppBar title={title} pageIcon={<Assignment />}>
                      <Grid
                        container
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                      >
                        <Grid item>
                          <Button
                            size="small"
                            variant="contained"
                            style={{ backgroundColor: '#e0e0e0', color: '#000' }}
                            onClick={() => this.handleCancelClick(dirty)}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            color="secondary"
                            size="small"
                            onClick={this.handleSave(submitForm, true)}
                            variant="contained"
                            disabled={
                              readonly || !isValid || this.state.isEOCSaved || !dirty
                            }
                          >
                            Save
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            size="small"
                            color="secondary"
                            variant="contained"
                            disabled={
                              readonly || !isValid || this.state.isEOCSaved || !dirty
                            }
                            onClick={this.handleSave(submitForm, false)}
                          >
                            Save and Close
                          </Button>
                        </Grid>
                      </Grid>
                    </ResponsiveAppBar>
                    <PatientActionBar isDemographicsVisible={true} />
                    <Grid>
                      <Grid item xs={12}>
                        <CardWithNoTitle>
                          <Grid container direction="row" spacing={2}>
                            <Grid item container xs={12}>
                              <Grid item container direction="row" spacing={2}>
                                <Grid item>
                                  <Typography
                                    color="primary"
                                    component="h6"
                                    variant="h6"
                                    style={{ paddingBottom: '20px' }}
                                  >
                                    EOC/Legacy:&nbsp;
                                    {episodeOfCare.episodeOfCareNumberOrLegacyId}
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <Button
                                    color="primary"
                                    variant="contained"
                                    size="small"
                                    style={{ whiteSpace: 'nowrap' }}
                                    onClick={this.props.openReferralContactModal}
                                  >
                                    Referral Contact
                                  </Button>
                                </Grid>
                                <Grid item style={{ paddingTop: '15px' }}>
                                  <Grid
                                    container
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <Grid item>
                                      {[
                                        episodeOfCare.lightDutyScheduleAppointmentTaskCompletionDate && {
                                          label: 'Light duty as of',
                                          date: episodeOfCare.lightDutyScheduleAppointmentTaskCompletionDate,
                                        },
                                        episodeOfCare.lightDutyPermanentScheduleAppointmentTaskCompletionDate && {
                                          label:
                                            'Light Duty (Permanent Restrictions) as of',
                                          date: episodeOfCare.lightDutyPermanentScheduleAppointmentTaskCompletionDate,
                                        },
                                        episodeOfCare.fullDutyScheduleAppointmentTaskCompletionDate && {
                                          label: 'Full duty as of',
                                          date: episodeOfCare.fullDutyScheduleAppointmentTaskCompletionDate,
                                        },
                                        episodeOfCare.unableToWorkScheduleAppointmentTaskCompletionDate && {
                                          label: 'Unable to work as of',
                                          date: episodeOfCare.unableToWorkScheduleAppointmentTaskCompletionDate,
                                        },
                                      ]
                                        .filter(Boolean)
                                        .sort(
                                          (a, b) =>
                                            moment(b?.date).valueOf() -
                                            moment(a?.date).valueOf()
                                        )
                                        .map((item) => (
                                          <Typography
                                            key={item?.label}
                                            variant="caption"
                                            color="primary"
                                            sx={{ pr: '10px' }}
                                          >
                                            <strong>
                                              {item?.label}{' '}
                                              {moment(item?.date).format('MM/DD/YYYY')}
                                            </strong>
                                          </Typography>
                                        ))}
                                    </Grid>
                                    {this.hasWorkRestrictionDates(episodeOfCare) && (
                                      <Grid item>
                                        <Tooltip title="Work Restriction Log">
                                          <ReceiptOutlined
                                            style={{ cursor: 'pointer' }}
                                            fontSize="small"
                                            onClick={this.handleWorkRestrictionLogClick}
                                          />
                                        </Tooltip>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid
                                item
                                container
                                direction="row"
                                spacing={2}
                                justifyContent="flex-start"
                                alignItems="flex-start"
                              >
                                <Grid item lg={2} md={6} xs={12}>
                                  <FullWidthField
                                    size="small"
                                    label="Body Part"
                                    name="bodyPartText"
                                    errorMessage={errors.bodyPartText}
                                    variant={'outlined'}
                                    disabled={readonly}
                                    required
                                    shrink={true}
                                  />
                                </Grid>
                                <Grid item lg={2.5} md={6} xs={12}>
                                  <Autocomplete
                                    size="small"
                                    id="referringDoctorId"
                                    options={referringDoctors || []}
                                    getOptionLabel={(option: IReferringDoctor) =>
                                      option
                                        ? `${option.name} ${option.address.phoneNumber}`
                                        : ''
                                    }
                                    value={
                                      referringDoctors?.find(
                                        (rd) => rd.id === values.referringDoctorId
                                      ) || null
                                    }
                                    onChange={(_, newValue) => {
                                      setFieldValue(
                                        'referringDoctorId',
                                        newValue ? newValue.id : ''
                                      )
                                      setFieldValue(
                                        'referringDoctorName',
                                        newValue
                                          ? `${newValue.name} ${newValue.address.phoneNumber}`
                                          : ''
                                      )
                                      setFieldValue('referringDoctor', newValue || null)
                                    }}
                                    disabled={readonly}
                                    renderInput={(params) => (
                                      <FullWidthField
                                        {...params}
                                        label="Referring Doctor"
                                        variant="outlined"
                                        name="referringDoctorName"
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        InputProps={{
                                          ...params.InputProps,
                                          style: { height: '40px' },
                                        }}
                                      />
                                    )}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item lg={2} md={6} xs={12}>
                                  {readonly ? (
                                    <FullWidthField
                                      InputProps={{
                                        style: { height: '40px' },
                                      }}
                                      variant="outlined"
                                      name="careCoordinatorName"
                                      label="Care Coordinator"
                                      disabled
                                    />
                                  ) : (
                                    <Autocomplete
                                      size="small"
                                      id="careCoordinatorId"
                                      options={users || []}
                                      getOptionLabel={(option: IUser) =>
                                        option ? this.getUserName(option) : ''
                                      }
                                      value={
                                        users?.find(
                                          (u) => u.id === values.careCoordinatorId
                                        ) || null
                                      }
                                      onChange={(_, newValue) => {
                                        setFieldValue(
                                          'careCoordinatorName',
                                          newValue ? this.getUserName(newValue) : ''
                                        )
                                        setFieldValue(
                                          'careCoordinatorId',
                                          newValue ? newValue.id : undefined
                                        )
                                      }}
                                      renderInput={(params) => (
                                        <FullWidthField
                                          {...params}
                                          label="Care Coordinator"
                                          variant="outlined"
                                          name="careCoordinatorName"
                                          required
                                          errorMessage={errors.careCoordinatorName}
                                          InputLabelProps={{ shrink: true }}
                                          fullWidth
                                        />
                                      )}
                                      fullWidth
                                    />
                                  )}
                                </Grid>
                                <Grid item lg={1.5} md={6} xs={12}>
                                  <Autocomplete
                                    size="small"
                                    id="coordinationStatusSelect"
                                    options={[
                                      CoordinationStatus.DMEOnly,
                                      CoordinationStatus.PTOnly,
                                    ]}
                                    getOptionLabel={(option: CoordinationStatus) =>
                                      CoordinationStatusLabels[option]
                                    }
                                    value={values.coordinationStatus ?? null}
                                    onChange={(_, newValue) =>
                                      setFieldValue(
                                        'coordinationStatus',
                                        newValue ?? undefined
                                      )
                                    }
                                    disabled={readonly}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Coordination Status"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                      />
                                    )}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item lg={1.5} md={4} xs={6}>
                                  <DatePickerField
                                    name="effectiveDate"
                                    label="Takeover of Care Date"
                                    inputId="effectiveDate"
                                    required={values.invoiceSurgeryStatus !== ''}
                                    clearable
                                    shrink={true}
                                    fullWidth
                                    variant="outlined"
                                    disabled={readonly}
                                  />
                                </Grid>
                                <Grid
                                  container
                                  direction="column"
                                  item
                                  lg={2.5}
                                  md={6}
                                  xs={12}
                                  spacing={1}
                                >
                                  <Grid item container direction="row" spacing={1}>
                                    <Grid item xs>
                                      <Autocomplete
                                        size="small"
                                        id="generateEmail"
                                        options={[
                                          {
                                            value: EOCEmailType.NewSurgeryEmail,
                                            label: 'New Surgery Email',
                                          },
                                          {
                                            value: EOCEmailType.NewEOCEmail,
                                            label: 'New EOC Email',
                                          },
                                        ]}
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(option, value) =>
                                          option.value === value.value
                                        }
                                        value={
                                          [
                                            {
                                              value: EOCEmailType.NewSurgeryEmail,
                                              label: 'New Surgery Email',
                                            },
                                            {
                                              value: EOCEmailType.NewEOCEmail,
                                              label: 'New EOC Email',
                                            },
                                          ].find(
                                            (opt) =>
                                              opt.value === this.state.generateEmail
                                          ) || undefined
                                        }
                                        onChange={(_, newValue) => {
                                          if (newValue) {
                                            this.handleGenerateEmailChange({
                                              target: { value: newValue.value },
                                            })
                                          }
                                        }}
                                        disabled={
                                          readonly ||
                                          (episodeOfCare && episodeOfCare.isNew)
                                        }
                                        disableClearable
                                        renderInput={(params) => (
                                          <FullWidthField
                                            {...params}
                                            label="Email Type"
                                            variant="outlined"
                                            name="generateEmail"
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                            size="small"
                                          />
                                        )}
                                      />
                                    </Grid>
                                    <Grid item xs="auto">
                                      <Tooltip title="Generate Email">
                                        <Button
                                          fullWidth
                                          style={{ height: '40px' }}
                                          color="secondary"
                                          disabled={
                                            (+this.state.generateEmail ===
                                              EOCEmailType.NewSurgeryEmail &&
                                              values.invoiceSurgeryStatus !==
                                                EOCStatus.SurgicalBundle &&
                                              values.invoiceSurgeryStatus !==
                                                EOCStatus.InjectionBundle) ||
                                            dirty
                                          }
                                          variant="contained"
                                          href={this.state.emailContent}
                                          onClick={this.generateEmailClick(values)}
                                        >
                                          <EmailOutlined />
                                        </Button>
                                      </Tooltip>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid
                                  item
                                  container
                                  direction="row"
                                  xs={12}
                                  justifyContent="flex-start"
                                  spacing={2}
                                >
                                  {this.props.episodeOfCare?.invoiceSurgeryStatus ==
                                    EOCStatus.PotentialNonSurgical && (
                                    <Grid item xs={12}>
                                      <Alert severity="error" sx={{ mb: 2 }}>
                                        This status is for finance, old status is listed
                                        in Comm Logs, please do not change
                                      </Alert>
                                    </Grid>
                                  )}
                                  {this.props.episodeOfCare?.quote &&
                                    this.props.episodeOfCare?.invoiceSurgeryStatus !==
                                      EOCStatus.PotentialNonSurgical &&
                                    quoteSummary && (
                                      <Grid item xs={12}>
                                        <Paper
                                          elevation={1}
                                          sx={{
                                            p: 2,
                                            mb: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                          }}
                                        >
                                          {quoteSummary.episodeOfCareQuoteSummaryMessage && (
                                            <>
                                              <Typography
                                                variant="body2"
                                                fontWeight={500}
                                                sx={{
                                                  mb:
                                                    (quoteSummary.totalPtCount ?? 0) >
                                                      0 ||
                                                    (quoteSummary.quotedPtCount ?? 0) > 0
                                                      ? 1.5
                                                      : 0,
                                                }}
                                              >
                                                {
                                                  quoteSummary.episodeOfCareQuoteSummaryMessage
                                                }
                                              </Typography>
                                              {((quoteSummary.totalPtCount ?? 0) > 0 ||
                                                (quoteSummary.quotedPtCount ?? 0) > 0 ||
                                                (quoteSummary.inHomePtCount ?? 0) > 0 ||
                                                (quoteSummary.inpatientRehabCount ?? 0) >
                                                  0 ||
                                                (quoteSummary.fceCount ?? 0) > 0 ||
                                                (quoteSummary.conditioningAndHardeningCount ??
                                                  0) > 0 ||
                                                (quoteSummary.otAndPtCount ?? 0) > 0) && (
                                                <Divider sx={{ mb: 1.5 }} />
                                              )}
                                            </>
                                          )}
                                          {((quoteSummary.totalPtCount ?? 0) > 0 ||
                                            (quoteSummary.quotedPtCount ?? 0) > 0 ||
                                            (quoteSummary.inHomePtCount ?? 0) > 0 ||
                                            (quoteSummary.inpatientRehabCount ?? 0) > 0 ||
                                            (quoteSummary.fceCount ?? 0) > 0 ||
                                            (quoteSummary.conditioningAndHardeningCount ??
                                              0) > 0 ||
                                            (quoteSummary.otAndPtCount ?? 0) > 0) && (
                                            <Box sx={{ width: '33.3%' }}>
                                              {(quoteSummary.inHomePtCount ?? 0) > 0 && (
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    In-Home PT Sessions
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight={500}
                                                  >
                                                    {quoteSummary.inHomePtCount}{' '}
                                                    authorized
                                                  </Typography>
                                                </Box>
                                              )}
                                              {(quoteSummary.inpatientRehabCount ?? 0) >
                                                0 && (
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    Inpatient Rehab Sessions
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight={500}
                                                  >
                                                    {quoteSummary.inpatientRehabCount}{' '}
                                                    authorized
                                                  </Typography>
                                                </Box>
                                              )}
                                              {(quoteSummary.fceCount ?? 0) > 0 && (
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    FCE Sessions
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight={500}
                                                  >
                                                    {quoteSummary.fceCount} authorized
                                                  </Typography>
                                                </Box>
                                              )}
                                              {(quoteSummary.conditioningAndHardeningCount ??
                                                0) > 0 && (
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    Work Conditioning/Hardening Sessions
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight={500}
                                                  >
                                                    {
                                                      quoteSummary.conditioningAndHardeningCount
                                                    }{' '}
                                                    authorized
                                                  </Typography>
                                                </Box>
                                              )}
                                              {(quoteSummary.otAndPtCount ?? 0) > 0 && (
                                                <>
                                                  <Divider sx={{ my: 1.5 }} />
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      justifyContent: 'space-between',
                                                      alignItems: 'center',
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="body2"
                                                      fontWeight={600}
                                                    >
                                                      Total PT/OT Sessions
                                                    </Typography>
                                                    <Box
                                                      sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                      }}
                                                    >
                                                      <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                      >
                                                        {quoteSummary.totalPtCount ?? 0}{' '}
                                                        authorized
                                                        {(quoteSummary.quotedPtCount ??
                                                          0) > 0 && (
                                                          <>
                                                            {' '}
                                                            of{' '}
                                                            {quoteSummary.quotedPtCount ??
                                                              0}{' '}
                                                            quoted
                                                          </>
                                                        )}
                                                      </Typography>
                                                      {(quoteSummary.quotedPtCount ?? 0) >
                                                        0 &&
                                                        (quoteSummary.totalPtCount ?? 0) >
                                                          (quoteSummary.quotedPtCount ??
                                                            0) && (
                                                          <Chip
                                                            label={`${(quoteSummary.totalPtCount ?? 0) - (quoteSummary.quotedPtCount ?? 0)} over`}
                                                            size="small"
                                                            color="error"
                                                            icon={<Warning />}
                                                          />
                                                        )}
                                                    </Box>
                                                  </Box>
                                                </>
                                              )}
                                            </Box>
                                          )}
                                        </Paper>
                                      </Grid>
                                    )}
                                  <Grid item lg={2} md={6} xs={12}>
                                    <Autocomplete
                                      size="small"
                                      id="invoiceSurgeryStatus"
                                      options={surgicalStatusOptions}
                                      getOptionLabel={(option) => option.label}
                                      value={
                                        surgicalStatusOptions.find(
                                          (opt) =>
                                            opt.value === values.invoiceSurgeryStatus
                                        ) || null
                                      }
                                      onChange={(_, newValue) => {
                                        setFieldValue(
                                          'invoiceSurgeryStatus',
                                          newValue ? newValue.value : ''
                                        )
                                      }}
                                      disabled={readonly}
                                      renderInput={(params) => (
                                        <FullWidthField
                                          {...params}
                                          label="Episode of Care Status"
                                          variant="outlined"
                                          name="invoiceSurgeryStatus"
                                          errorMessage={errors.invoiceSurgeryStatus}
                                          InputLabelProps={{ shrink: true }}
                                          fullWidth
                                        />
                                      )}
                                    />
                                  </Grid>
                                  <Grid item style={{ display: 'flex', gap: '15px' }}>
                                    {quoteSummary &&
                                      quoteSummary?.buttons.map((button) => (
                                        <Tooltip
                                          key={button.quoteId}
                                          title={
                                            button.toolTipMessage ||
                                            'No tooltip available'
                                          }
                                          placement="right-start"
                                        >
                                          <Button
                                            color={
                                              button.isPrimaryQuote
                                                ? 'primary'
                                                : 'secondary'
                                            }
                                            variant="contained"
                                            size="small"
                                            onClick={this.navigateToQuote(button.quoteId)}
                                            style={{
                                              whiteSpace: 'nowrap',
                                              width: 'auto',
                                            }}
                                          >
                                            {this.renderQuoteButtonIcon(button)}
                                            {this.getQuoteButtonLabel(
                                              button,
                                              quoteSummary.buttons.length
                                            )}
                                          </Button>
                                        </Tooltip>
                                      ))}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                          <>
                            <Grid item xs={12}>
                              <Accordion
                                style={{
                                  marginTop: '10px',
                                  marginBottom: '10px',
                                }}
                                expanded={
                                  this.state.expandedDetailsPanel === 'detailsPanel'
                                }
                                onChange={this.handleDetailsChange('detailsPanel')}
                              >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography color="textSecondary" variant="body1">
                                    EPISODE OF CARE DETAILS
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Grid item container xs={12} spacing={2}>
                                    <Grid item md={1} xs={3}>
                                      <SelectField
                                        inputId="mri"
                                        getName={this.getValue}
                                        getValue={this.getValue}
                                        items={['Yes', 'No']}
                                        label="MRI"
                                        name="mri"
                                        fullWidth
                                        outlined
                                        disabled={readonly}
                                      />
                                    </Grid>
                                    <Grid item md={1} xs={3}>
                                      <SelectField
                                        inputId="emg"
                                        getName={this.getValue}
                                        getValue={this.getValue}
                                        items={['Yes', 'No']}
                                        label="EMG"
                                        name="emg"
                                        fullWidth
                                        outlined
                                        disabled={readonly}
                                      />
                                    </Grid>
                                    <Grid item md={1} xs={3}>
                                      <SelectField
                                        inputId="xRay"
                                        getName={this.getValue}
                                        getValue={this.getValue}
                                        items={['Yes', 'No']}
                                        label="X-Ray"
                                        name="xRay"
                                        fullWidth
                                        outlined
                                        disabled={readonly}
                                      />
                                    </Grid>
                                    <Grid item md={1} xs={3}>
                                      <SelectField
                                        inputId="ctScan"
                                        getName={this.getValue}
                                        getValue={this.getValue}
                                        items={['Yes', 'No']}
                                        label="CT Scan"
                                        name="ctScan"
                                        fullWidth
                                        outlined
                                        disabled={readonly}
                                      />
                                    </Grid>
                                    <Grid item lg={2} md={3} xs={12}>
                                      <Field
                                        type="checkbox"
                                        name="isClinicalNotesReceived"
                                        Label={{
                                          label: 'Clinical Notes Received',
                                        }}
                                        component={CheckboxWithLabel}
                                        disabled={readonly}
                                      />
                                    </Grid>
                                    <Grid item lg={2} md={3} xs={12}>
                                      <Field
                                        type="checkbox"
                                        name="isPatientOnBoarding"
                                        Label={{
                                          label: 'Patient Onboarding',
                                        }}
                                        component={CheckboxWithLabel}
                                        disabled={readonly}
                                      />
                                    </Grid>
                                    <Grid item lg={3} md={3} xs={12}>
                                      <Field
                                        type="checkbox"
                                        name="isNoPtToBeScheduled"
                                        Label={{
                                          label: 'No PT to be Scheduled for this Patient',
                                        }}
                                        component={CheckboxWithLabel}
                                        disabled={readonly}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <CodeExpandingList
                                        type="cptCodes"
                                        disabled={readonly}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <CodeExpandingList
                                        type="dxCodes"
                                        disabled={readonly}
                                      />
                                    </Grid>
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>
                          </>
                          <Grid item container direction="row" spacing={2}>
                            <Grid item md={6} xs={12}>
                              <FullWidthField
                                variant="outlined"
                                name="notes"
                                label="EOC Notes"
                                fullWidth
                                rows={8}
                                multiline
                                disabled={readonly}
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <FullWidthField
                                variant="outlined"
                                name="claimNotes"
                                label="Billing Notes"
                                fullWidth
                                rows={8}
                                multiline
                                disabled={readonly}
                              />
                            </Grid>
                          </Grid>
                          {episodeOfCare && !episodeOfCare.isNew ? (
                            <Grid item xs={12}>
                              <Accordion
                                style={{ marginTop: '10px' }}
                                expanded={this.state.expandedLogPanel === 'logPanel'}
                                onChange={this.handleLogChange('logPanel')}
                              >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography color="textSecondary" variant="body1">
                                    AUTHORIZATION AND CONTRACT LOG
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Grid
                                    item
                                    container
                                    xs={12}
                                    spacing={1}
                                    style={{ paddingBottom: '10px' }}
                                  >
                                    <AuthorizationLogGrid
                                      episodeOfCareId={episodeOfCare.id}
                                      onDataChanged={this.refreshPtCounts}
                                    />
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                              <Accordion
                                style={{ marginTop: '10px' }}
                                expanded={
                                  this.state.expandedSchedulePanel === 'schedulePanel'
                                }
                                onChange={this.handleScheduleChange('schedulePanel')}
                              >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography color="textSecondary" variant="body1">
                                    SCHEDULE INFORMATION
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Grid
                                    item
                                    container
                                    xs={12}
                                    spacing={1}
                                    style={{ paddingBottom: '10px' }}
                                  >
                                    <SchedulingTable
                                      episodeOfCareId={episodeOfCare.id}
                                      referralId={this.props.referralId}
                                      bodyPart={episodeOfCare.bodyPartText}
                                    />
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>
                          ) : (
                            <></>
                          )}
                        </CardWithNoTitle>
                      </Grid>
                    </Grid>

                    {isCodeCheckModalOpen ? (
                      this.codeDialog(
                        newCodeFormValues,
                        isCodeCheckModalOpen,
                        episodeOfCare
                      )
                    ) : (
                      <></>
                    )}
                  </Form>
                )
              }}
            </Formik>
          </>
        )}
      </>
    )
  }

  private codeDialog = (
    initialValues: INewSurgeryCodeFormValues,
    isCodeCheckModalOpen: boolean,
    episodeOfCare: IEpisodeOfCare
  ) => {
    return (
      <ESDialog
        close={this.props.closeCodeCheckModal!}
        initialValues={initialValues}
        open={isCodeCheckModalOpen!}
        save={this.saveNewCodes(episodeOfCare)}
        title={'Code(s) Not Found'}
        maxWidth="sm"
        titleIcon={<Code />}
        closeButtonText="Not Now"
        isInitialValid={true}
      >
        {({ values }) => (
          <Grid container direction="column" spacing={3} style={{ paddingTop: '20px' }}>
            <Grid item xs={12}>
              <span>
                One or more of the CPT and DX Codes used was not found in the database.
                Please enter a description for each missing code below to add the code and
                description to the database.
              </span>
            </Grid>
            <Grid item xs={12}>
              <h2>CPT Codes</h2>
            </Grid>
            {values.cptCodes.map((x, idx) => {
              return (
                <Grid key={x.code} item xs={12}>
                  <Grid container direction="row" spacing={1}>
                    <Grid item xs={4}>
                      <FullWidthField
                        autoFocus
                        InputLabelProps={{ shrink: true }}
                        name={`cptCodes[${idx}].code`}
                        label={idx === 0 ? 'CPT Code' : undefined}
                        disabled
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <FullWidthField
                        InputLabelProps={{ shrink: true }}
                        name={`cptCodes[${idx}].description`}
                        label={idx === 0 ? 'Description' : undefined}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )
            })}
            <Grid item xs={12}>
              <h2>DX Codes</h2>
            </Grid>
            {values.dxCodes.map((x, idx) => {
              return (
                <Grid key={x.code} item xs={12}>
                  <Grid container direction="row" spacing={1}>
                    <Grid item xs={4}>
                      <FullWidthField
                        InputLabelProps={{ shrink: true }}
                        name={`dxCodes[${idx}].code`}
                        label={idx === 0 ? 'DX Code' : undefined}
                        disabled
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <FullWidthField
                        InputLabelProps={{ shrink: true }}
                        name={`dxCodes[${idx}].description`}
                        label={idx === 0 ? 'Description' : undefined}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )
            })}
          </Grid>
        )}
      </ESDialog>
    )
  }

  private saveNewCodes =
    (episodeOfCare: IEpisodeOfCare) =>
    async (
      values: INewSurgeryCodeFormValues,
      formikBag: FormikHelpers<INewSurgeryCodeFormValues>
    ) => {
      const cptCodes = values.cptCodes
        .filter((x) => x.description !== undefined && x.description !== '')
        .map(
          (x) =>
            new CptCodeDto({
              ...DefaultCptCode(true),
              code: x.code,
              description: x.description,
            })
        )
      const dxCodes = values.dxCodes
        .filter((x) => x.description !== undefined && x.description !== '')
        .map(
          (x) =>
            new DxCodeDto({
              ...DefaultDxCode(true),
              code: x.code,
              description: x.description,
            })
        )

      const promises = [] as Array<Promise<void>>

      cptCodes.forEach((x) => {
        promises.push(this.props.saveCptCode!({ ...x } as ICptCode))
      })

      dxCodes.forEach((x) => {
        promises.push(this.props.saveDxCode!({ ...x } as IDxCode))
      })

      await Promise.all(promises).then(() => {
        formikBag.setSubmitting(false)
        this.props.closeCodeCheckModal!()
        if (this.state.reload) {
          this.props.rerouteToPath!(
            `/patients/patient/${this.props.patientId}/referral/${this.props.referralId}/episodeOfCare/${episodeOfCare.id}`
          )
          this.setup()
        } else {
          this.cancel()
        }
      })
    }

  private navigateToQuote = (quoteId: string) => () => {
    window.open(`/workflow/quote/${quoteId}/default-inbox`, '_blank')
  }
}

const InjectedEditEpisodeOfCare = inject<
  IStores,
  IEditEpisodeOfCareProps,
  Partial<IEditEpisodeOfCareProps>,
  any
>((stores: IStores) => ({
  bodyParts: stores.patientEdit.bodyParts,
  closeCodeCheckModal: stores.patientEdit.closeCodeCheckModal,
  closeTab: stores.global.closeTab,
  cptCodes: stores.cptCodes.cptCodes,
  currentAppOrganization: stores.global.currentAppOrganization,
  dxCodes: stores.dxCodes.dxCodes,
  episodeOfCare: stores.patientEdit.selectedEpisodeOfCare,
  getAllCptCodes: stores.cptCodes.getAllCptCodes,
  getAllDxCodes: stores.dxCodes.getAllDxCodes,
  getAllProcedures: stores.procedures.getAllProcedures,
  getAllUsers: stores.users.getAllUsers,
  getEpisodeOfCareById: stores.patientEdit.getEpisodeOfCareById,
  getPatientById: stores.patients.getPatientById,
  getReferringDoctorById: stores.referringDoctors.getReferringDoctorById,
  getReferringDoctors: stores.referringDoctors.getAllReferringDoctors,
  isCodeCheckModalOpen: stores.patientEdit.isCodeCheckModalOpen,
  newCptCodesToSave: stores.cptCodes.newCptCodesToSave,
  newDxCodesToSave: stores.dxCodes.newDxCodesToSave,
  openCodeCheckModal: stores.patientEdit.openCodeCheckModal,
  referringDoctors: stores.referringDoctors.referringDoctors,
  returnRoutePath: stores.global.returnRoutePath,
  rerouteToPath: stores.global.rerouteToPath,
  saveCptCode: stores.cptCodes.saveCptCode,
  saveDxCode: stores.dxCodes.saveDxCode,
  saveEpisodeOfCare: stores.patientEdit.saveEpisodeOfCare,
  setNewCptCodesToSave: stores.cptCodes.setNewCptCodesToSave,
  setNewDxCodesToSave: stores.dxCodes.setNewDxCodesToSave,
  setReturn: stores.global.setReturn,
  setSelectedEpisodeOfCare: stores.patientEdit.setSelectedEpisodeOfCare,
  setSelectedPatient: stores.patients.setSelectedPatient,
  patient: stores.patients.selectedPatient,
  users: stores.users.users,
  eocToDashboard: stores.schedules.eocToDashboard,
  eocToQuotes: stores.schedules.eocToQuotes,
  resetEOC: stores.schedules.resetEOC,
  openReferralContactModal: stores.patientEdit.openReferralContactModal,
  isReferralContactModalOpen: stores.patientEdit.isReferralContactModalOpen,
}))(EditEpisodeOfCare)

const EditEpisodeOfCareWithParams = (
  props: Omit<
    IEditEpisodeOfCareProps,
    | 'patientId'
    | 'referralId'
    | 'episodeOfCareId'
    | 'showCancelDialog'
    | 'handleCancelClick'
    | 'handleCancelConfirm'
    | 'handleCancelDismiss'
    | 'setIsDirty'
  >
) => {
  const { patientId, referralId, episodeOfCareId } = useParams()
  const [isDirty, setIsDirty] = React.useState(false)
  const componentRef = React.useRef<EditEpisodeOfCare>(null)

  // Use shared unsaved changes hook
  const {
    showCancelDialog,
    handleCancelClick,
    handleCancelConfirm,
    handleCancelDismiss,
  } = useUnsavedChangesWarning(isDirty)

  const handleCancel = () => {
    if (componentRef.current) {
      ;(componentRef.current as any).cancel()
    }
  }

  return (
    <>
      <InjectedEditEpisodeOfCare
        {...props}
        ref={componentRef}
        patientId={patientId}
        referralId={referralId}
        episodeOfCareId={episodeOfCareId}
        showCancelDialog={showCancelDialog}
        handleCancelClick={handleCancelClick}
        handleCancelConfirm={(onNavigate) => {
          handleCancelConfirm(onNavigate)
        }}
        handleCancelDismiss={handleCancelDismiss}
        setIsDirty={setIsDirty}
      />
      <UnsavedChangesDialog
        open={showCancelDialog}
        onConfirm={() => handleCancelConfirm(handleCancel)}
        onDismiss={handleCancelDismiss}
      />
    </>
  )
}

export default EditEpisodeOfCareWithParams
